/**
 * Activity Tab Component
 *
 * Displays timestamped activity entries (notes/comments) for templates or instances.
 * - Input field at top: "Add a note..." textarea, auto-save on blur
 * - List of existing entries below (newest first)
 * - Edit/delete buttons only for own entries
 * - Edit inline: click edit turns text into textarea, save on blur
 */

import { useState, useRef, useCallback } from 'react';
import { Input, Button, Space, Typography, Avatar, Popconfirm, message, Spin, Empty } from 'antd';
import { EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { activityEntriesApi } from '@api/activity-entries';
import { useAuthStore } from '@/store/authStore';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ActivityTabProps {
  entityType: 'template' | 'instance';
  entityId: string;
}

export default function ActivityTab({ entityType, entityId }: ActivityTabProps) {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [newContent, setNewContent] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const editRef = useRef<any>(null);

  // Fetch entries
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['activity-entries', entityType, entityId],
    queryFn: () => activityEntriesApi.list(entityType, entityId),
    enabled: !!entityId,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (content: string) =>
      activityEntriesApi.create({ entity_type: entityType, entity_id: entityId, content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-entries', entityType, entityId] });
      setNewContent('');
    },
    onError: () => message.error('Failed to add note'),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      activityEntriesApi.update(id, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-entries', entityType, entityId] });
      setEditingId(null);
    },
    onError: () => message.error('Failed to update note'),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => activityEntriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-entries', entityType, entityId] });
    },
    onError: () => message.error('Failed to delete note'),
  });

  // Auto-save on blur for new entry
  const handleNewBlur = useCallback(() => {
    const trimmed = newContent.trim();
    if (trimmed.length > 0) {
      createMutation.mutate(trimmed);
    }
  }, [newContent, createMutation]);

  // Auto-save on blur for editing
  const handleEditBlur = useCallback(() => {
    if (!editingId) return;
    const trimmed = editContent.trim();
    if (trimmed.length > 0) {
      updateMutation.mutate({ id: editingId, content: trimmed });
    } else {
      setEditingId(null);
    }
  }, [editingId, editContent, updateMutation]);

  // Start editing
  const handleStartEdit = useCallback((id: string, content: string) => {
    setEditingId(id);
    setEditContent(content);
    setTimeout(() => editRef.current?.focus(), 50);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* New entry input */}
      <TextArea
        value={newContent}
        onChange={(e) => setNewContent(e.target.value)}
        onBlur={handleNewBlur}
        placeholder="Add a note..."
        autoSize={{ minRows: 2, maxRows: 6 }}
        disabled={createMutation.isPending}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleNewBlur();
          }
        }}
      />

      {/* Loading state */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin size="small" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && entries.length === 0 && (
        <Empty description="No activity notes yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}

      {/* Entry list */}
      {entries.map((entry) => (
        <div
          key={entry.id}
          style={{
            display: 'flex',
            gap: 10,
            padding: '8px 0',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Avatar
            size="small"
            src={entry.creatorAvatarUrl}
            icon={!entry.creatorAvatarUrl && <UserOutlined />}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space size={4}>
                <Text strong style={{ fontSize: 13 }}>
                  {entry.creatorName || 'Unknown User'}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {dayjs(entry.createdAt).fromNow()}
                </Text>
                {entry.updatedAt !== entry.createdAt && (
                  <Text type="secondary" style={{ fontSize: 11 }}>(edited)</Text>
                )}
              </Space>
              {entry.createdBy === currentUserId && editingId !== entry.id && (
                <Space size={4}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleStartEdit(entry.id, entry.content)}
                  />
                  <Popconfirm
                    title="Delete this note?"
                    onConfirm={() => deleteMutation.mutate(entry.id)}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                  >
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              )}
            </div>
            {editingId === entry.id ? (
              <TextArea
                ref={editRef}
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onBlur={handleEditBlur}
                autoSize={{ minRows: 2, maxRows: 6 }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleEditBlur();
                  }
                  if (e.key === 'Escape') {
                    setEditingId(null);
                  }
                }}
              />
            ) : (
              <Paragraph
                style={{ marginBottom: 0, fontSize: 13, whiteSpace: 'pre-wrap' }}
              >
                {entry.content}
              </Paragraph>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
